import { Tag, TagsDocument } from "@/types/v2/Tags";
import { generic } from "@/repositories/generic";
import shortUUID from "short-uuid";
import { firestore } from "@/services/firestore";
import { TAG_COLORS } from "@/lib/const";

export const TagsRepository = {
  id: "tags",
  path: (shopId: string) => `shops/${shopId}/misc`,
  addTag: (shopId: string, tags: string | string[], tagsDoc?: TagsDocument) => {
    let newTags: string[];
    if (typeof tags === "string") {
      newTags = [tags];
    } else {
      newTags = tags;
    }
    if (!tagsDoc) {
      const tagsForDoc: Tag[] = [];
      for (const tag of newTags) {
        const tagItem: Tag = {
          id: shortUUID.generate(),
          label: tag,
          customColor: TagsRepository.getRandomTagColor(),
        };
        tagsForDoc.push(tagItem);
      }
      return TagsRepository.create(shopId, tagsForDoc);
    }
    const docTags = tagsDoc.tags;
    for (const tag of newTags) {
      if (
        !docTags.map((t) => t.label.toLowerCase()).includes(tag.toLowerCase())
      ) {
        docTags.push({
          id: shortUUID.generate(),
          label: tag,
          customColor: TagsRepository.getRandomTagColor(),
        });
      }
    }
    tagsDoc.tags = docTags;
    return TagsRepository.update(shopId, tagsDoc);
  },
  removeTag: async (shopId: string, tag: Tag, tagsDoc?: TagsDocument) => {
    if (!tagsDoc) {
      tagsDoc = await TagsRepository.get(shopId);
      if (!tagsDoc) {
        return;
      }
    }
    const tags = tagsDoc.tags;
    const index = tags.findIndex((t) => t.id === tag.id);
    if (index > -1) {
      tags.splice(index, 1);
    }
    return TagsRepository.update(shopId, tagsDoc);
  },
  /**
   * Rewrites the shop's tag list in `orderedIds` order — the array's index is
   * the shared display order every card's tag chips render in.
   *
   * Ids the doc no longer has are skipped, and tags the caller didn't mention
   * keep their existing relative order at the end, so a stale drag can't drop
   * a tag. Skips the write entirely when the order is already what was asked
   * for, so a drop that lands where it started costs nothing.
   */
  reorderTags: (
    shopId: string,
    orderedIds: string[],
    tagsDoc: TagsDocument
  ) => {
    const byId = new Map(tagsDoc.tags.map((tag) => [tag.id, tag]));
    const reordered = orderedIds
      .map((id) => byId.get(id))
      .filter((tag): tag is Tag => tag !== undefined);
    const requested = new Set(reordered.map((tag) => tag.id));
    const tags = [
      ...reordered,
      ...tagsDoc.tags.filter((tag) => !requested.has(tag.id)),
    ];

    const unchanged = tags.every(
      (tag, index) => tag.id === tagsDoc.tags[index].id
    );
    if (unchanged) return;

    return TagsRepository.update(shopId, { ...tagsDoc, tags });
  },
  create: async (shopId: string, tags: Tag[]) => {
    const tagsDoc = await TagsRepository.get(shopId);
    if (!tagsDoc) {
      const tagsDoc = {
        id: TagsRepository.id,
        tags: tags,
      };
      return await generic.create<TagsDocument>(
        tagsDoc,
        `${TagsRepository.path(shopId)}/${TagsRepository.id}`
      );
    }
    return tagsDoc;
  },
  get: (shopId: string) => {
    return generic.get<TagsDocument>(
      TagsRepository.id,
      TagsRepository.path(shopId)
    );
  },
  update: (shopId: string, tagDoc: TagsDocument) => {
    return generic.update<TagsDocument>(
      tagDoc,
      `${TagsRepository.path(shopId)}/${TagsRepository.id}`
    );
  },
  subscribe: (
    shopId: string,
    callback: (doc: TagsDocument | undefined) => void
  ) => {
    return firestore.subscribeDocument<TagsDocument>(
      `${TagsRepository.path(shopId)}/${TagsRepository.id}`,
      callback
    );
  },
  getRandomTagColor(): string {
    const randomizableColors = TAG_COLORS.filter(
      (color) => color !== "#FFFFFF"
    );
    const randomIndex = Math.floor(Math.random() * randomizableColors.length);
    return randomizableColors[randomIndex];
  },
};
